import { FC, useRef } from 'react';

import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { Settings } from 'utils/constants/dynamicSettings';

import { Button, HSpacingContainer, Text, VSpacingContainer } from '@hh.ru/magritte-ui';
import { EllipsisHorizontalOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import FormatMoney from 'components/FormatMoney';

import { AdditionalActions } from 'components/common/BalanceCardContent/BalanceCardTop/AdditionalActions/AdditionalActions';

import { SessionType } from 'store/models/user';
import { useSelector } from 'store/utils';

import styles from 'components/common/BalanceCardContent/BalanceCardTop/BalanceCardTop.module.less';

const TrlKeys = {
    balance: 'Header.HeaderBalanceDrop.balance',
    topUp: 'Header.HeaderBalanceDrop.topUp',
};

export const BalanceCardTop: FC<{ onTopUpButtonClick: () => void }> = ({ onTopUpButtonClick }) => {
    const isSudo = useSelector((state) => state.user?.sessionType === SessionType.Sudo);
    const [isAdditionalActionsVisible, setIsAdditionalActionsVisibleOn, setIsAdditionalActionsVisibleOff] =
        useOnOffState(false);

    const { realBalance } = useSelector(({ balance }) => balance);

    const trls = useTrls();
    const activatorRef = useRef<HTMLButtonElement>(null);
    const settings = useSettings();

    const isUserRefundEnabled = settings(Settings.EnableUserRefund) as boolean;

    const hasAdditionalActions = isUserRefundEnabled || isSudo;

    return (
        <div className={styles.dropTopWrapper}>
            <VSpacingContainer default={6}>
                <Text typography="label-2-regular" style="secondary">
                    {trls(TrlKeys.balance)}
                </Text>
                <Text typography="title-4-semibold" data-qa="balance-card-balance">
                    <FormatMoney groupSeparator={NON_BREAKING_SPACE}>{realBalance}</FormatMoney>
                </Text>
            </VSpacingContainer>
            <HSpacingContainer default={8}>
                {hasAdditionalActions && (
                    <Button
                        ref={activatorRef}
                        size="large"
                        mode="secondary"
                        style="neutral"
                        hideLabel
                        icon={<EllipsisHorizontalOutlinedSize24 />}
                        onClick={setIsAdditionalActionsVisibleOn}
                    />
                )}
                <Button
                    mode="primary"
                    size="large"
                    onClick={onTopUpButtonClick}
                    data-qa="header-balance-drop-top-up-button"
                >
                    {trls(TrlKeys.topUp)}
                </Button>
            </HSpacingContainer>
            {hasAdditionalActions && (
                <AdditionalActions
                    activatorRef={activatorRef}
                    visible={isAdditionalActionsVisible}
                    onClose={setIsAdditionalActionsVisibleOff}
                    isUserRefundEnabled={isUserRefundEnabled}
                    isReBalanceButtonEnabled={isSudo}
                />
            )}
        </div>
    );
};
